import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Form, Modal, Spin, Tabs, message } from 'antd';
import { editRenderingProviders } from 'api/insuranceBilling';
import { clearProviderEmailCheckCache } from 'components/form/ProviderEmailInput';
import useCurrentUser from 'hooks/useCurrentUser';
import useEhrConnections from 'hooks/useEhrConnections';
import ProviderStore from 'store/Provider';
import type { BillingStepKey } from '../../steps';
import { renderBillingStep } from '../../steps';
import { fieldMarkableErrors, getSectionMissingFields, REVIEW_SECTIONS } from '../../reviewSpec';
import ReviewSection from '../ReviewSection';
import EhrScopeSettings from './EhrScopeSettings';
import { ManagedBillingSection, onlyConnectedEhrs, useManagedBilling } from '../ManagedBilling';
import {
    fetchOnboardingVo,
    hydrateManualTaxonomyOptions,
    hydratePaymentMethod,
    onboardingDtoToFormValues,
    STEP_API,
    STEP_FIELDS,
    stepValuesToDto,
} from '../../billingReviewData';
import s from './s.module.less';

interface Props {
    open: boolean;
    onClose: () => void;
    // 任一 section 保存成功后回调（外层可刷新展示数据）
    onSaveSuccess?: () => void;
    initialTab?: BillingStepKey;
}

// 提交后的 billing 信息查看/编辑弹窗：Tabs 按 review section 分页，
// 每个 tab 复用 ReviewSection + 对应步骤表单（与 review 页同一套组件），逐 section 保存
export default function BillingInfoModal({ open, onClose, onSaveSuccess, initialTab }: Props) {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<BillingStepKey>(initialTab || 'billing-provider');
    const [loading, setLoading] = useState(false);
    const [editingSections, setEditingSections] = useState<BillingStepKey[]>([]);
    const [savingSection, setSavingSection] = useState<BillingStepKey | null>(null);
    // Cancel 时回滚的整表快照
    const snapshotRef = useRef<Record<string, any> | null>(null);
    // 触发只读视图重算（表单值不在 state 里）
    const [, forceRefresh] = useState(0);
    const [currentUser] = useCurrentUser();
    const [getUser] = ProviderStore.useStore();
    // Payment tab 底部的 Managed billing 区块与 EHR tab 的 Not billed 标签共用一份状态（打开时拉一次）
    const managed = useManagedBilling(open);
    const [connections] = useEhrConnections();
    const managedState = useMemo(
        () => (managed.state ? onlyConnectedEhrs(managed.state, connections) : null),
        [managed.state, connections],
    );

    // 打开时 hydrate 一次
    useEffect(() => {
        if (!open) return undefined;
        setActiveTab(initialTab || 'billing-provider');
        setEditingSections([]);
        let cancelled = false;
        setLoading(true);
        (async () => {
            const vo = await fetchOnboardingVo();
            if (cancelled) return;
            form.resetFields();
            form.setFieldsValue(onboardingDtoToFormValues(vo));
            // 已存卡回填须在 resetFields 之后写入（Payment tab 只读视图读 payment.cardOnFile）
            await Promise.all([hydratePaymentMethod(form), hydrateManualTaxonomyOptions(form)]);
            if (cancelled) return;
            snapshotRef.current = form.getFieldsValue(true);
            setLoading(false);
            forceRefresh((n) => n + 1);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const openSectionEdit = (key: BillingStepKey) => {
        snapshotRef.current = form.getFieldsValue(true);
        setEditingSections((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };

    const cancelSectionEdit = (key: BillingStepKey) => {
        if (snapshotRef.current) form.setFieldsValue(snapshotRef.current);
        setEditingSections((prev) => prev.filter((k) => k !== key));
        forceRefresh((n) => n + 1);
    };

    // rendering tab 走 settings 专用入口：整表替换 + 顺带给新条目建 team 成员，返回完整 VO。
    // 错误文案由全局 toast 展示，这里只处理副作用
    const saveRenderingProviders = async (body: any) => {
        const res: any = await editRenderingProviders(body);
        if (res?.error) {
            // 91023 名单校验失败：过期的 email「可用」判定可能正是失败原因，清缓存让用户重查
            if (res?.errorCode === 91023) clearProviderEmailCheckCache();
            return false;
        }
        // 用返回 VO 回填：新建条目的 id / bpProviderId 已回写，缺了会导致下次整表替换重复建号
        const vo = res?.data?.data;
        if (vo) form.setFieldsValue(onboardingDtoToFormValues(vo));
        return true;
    };

    const saveSectionEdit = async (key: BillingStepKey) => {
        const current = form.getFieldsValue(true);
        const missing = getSectionMissingFields(current, key);
        if (missing.length > 0) {
            // 拦截按全部错误算；标红剔除 NpiRegistryBox 已内联展示的，避免重复红字
            form.setFields(fieldMarkableErrors(missing));
            form.scrollToField(missing[0].name, { behavior: 'smooth', block: 'center' });
            message.error('Please complete the required fields before saving.');
            return;
        }
        setSavingSection(key);
        try {
            const body = stepValuesToDto(key, form.getFieldsValue(STEP_FIELDS[key]));
            if (key === 'rendering-providers') {
                const ok = await saveRenderingProviders(body);
                if (!ok) return;
            } else {
                const res: any = await STEP_API[key].save(body);
                // 失败时错误已由全局 toast 展示，保持编辑态供用户修正
                if (res?.error) return;
            }
            snapshotRef.current = form.getFieldsValue(true);
            setEditingSections((prev) => prev.filter((k) => k !== key));
            message.success('Changes saved.');
            forceRefresh((n) => n + 1);
            onSaveSuccess?.();
        } finally {
            setSavingSection((cur) => (cur === key ? null : cur));
        }
    };

    const handleClose = () => {
        // 有未保存的编辑时回滚，避免脏数据留在表单里
        if (editingSections.length && snapshotRef.current) form.setFieldsValue(snapshotRef.current);
        setEditingSections([]);
        onClose();
    };

    const values = form.getFieldsValue(true);

    // 原型 settings：rendering tab 读态直接带 "+ Add another provider"（追加空 provider 并进入编辑）
    const addProviderFromRead = () => {
        const current = form.getFieldValue(['rendering', 'providers']) || [{}];
        form.setFieldValue(['rendering', 'providers'], [...current, { sameAsBilling: 'no' }]);
        openSectionEdit('rendering-providers');
    };

    // ehr tab 是内联管理视图（原型 editable = key !== 'ehr-scope'），不走 ReviewSection 的读/编辑模式
    const renderTabBody = (sec: (typeof REVIEW_SECTIONS)[number]) => {
        if (sec.key === 'claim-scope') {
            return (
                <EhrScopeSettings
                    onSaveSuccess={onSaveSuccess}
                    notBilledEhrs={managedState?.ehrs.filter((e) => !e.on).map((e) => e.ehr)}
                />
            );
        }
        let readExtra: React.ReactNode;
        if (sec.key === 'rendering-providers') {
            readExtra = (
                <Button className={s.readAddBtn} onClick={addProviderFromRead}>
                    + Add another provider
                </Button>
            );
        } else if (sec.key === 'payment-method') {
            readExtra = (
                <ManagedBillingSection
                    state={managedState}
                    loading={managed.loading}
                    isGroup={!!currentUser?.groupPractice}
                    selfProviderId={getUser('data')?.id}
                    onChange={(next) => {
                        managed.setState(next);
                        onSaveSuccess?.();
                    }}
                />
            );
        }
        return (
            <ReviewSection
                sectionKey={sec.key}
                title={sec.title}
                values={values}
                editing={editingSections.includes(sec.key)}
                saving={savingSection === sec.key}
                onEdit={() => openSectionEdit(sec.key)}
                onCancel={() => cancelSectionEdit(sec.key)}
                onSave={() => saveSectionEdit(sec.key)}
                readExtra={readExtra}
            >
                {renderBillingStep(sec.key)}
            </ReviewSection>
        );
    };

    return (
        <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
            <Modal
                open={open}
                onCancel={handleClose}
                footer={null}
                width={1040}
                title={
                    <div>
                        Claim settings
                        <div className={s.modalTitleSub}>View and update your billing information.</div>
                    </div>
                }
                destroyOnClose
            >
                <Spin spinning={loading}>
                    <Form form={form} layout="vertical" requiredMark={false} className={s.formWrap}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => setActiveTab(key as BillingStepKey)}
                            items={REVIEW_SECTIONS.map((sec) => ({
                                key: sec.key,
                                label: sec.title,
                                children: <div className={s.tabBody}>{renderTabBody(sec)}</div>,
                            }))}
                        />
                    </Form>
                </Spin>
            </Modal>
        </ConfigProvider>
    );
}
